import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, fmtDateShort } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import s from "./AttendanceTab.module.css";
import { syLabel } from "../../../../utils/schoolYear";

const STATUS_COLOR = {
  PRESENT: { bg: "#e8f6ed", color: "#1e6f3e" },
  LATE:    { bg: "#fdf3d4", color: "#8a6d28" },
  ABSENT:  { bg: "#fde8e4", color: "#c0392b" },
  EXCUSED: { bg: "#e8eef9", color: "#2b4d8a" },
};

export default function AttendanceTab({ studentId, schoolYears = [], semesters = [] }) {
  const [attendance, setAttendance] = useState([]);
  const [offerings,  setOfferings]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [expanded,   setExpanded]   = useState(new Set());

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");
    Promise.all([
      fetch(`${API_BASE}/api/attendance?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
    ])
      .then(([attData, offerData]) => {
        if (cancelled) return;
        setAttendance(Array.isArray(attData)  ? attData  : []);
        setOfferings(Array.isArray(offerData) ? offerData : []);
      })
      .catch(() => { if (!cancelled) setError("Could not load attendance records."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const offeringById = useMemo(() => {
    const map = {};
    offerings.forEach(o => { map[o.id] = o; });
    return map;
  }, [offerings]);

  const syById  = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const semById = useMemo(() => Object.fromEntries(semesters.map(m => [m.id, m])),   [semesters]);

  /* Group: schoolYearId → semesterId → classOfferingId → records[] */
  const grouped = useMemo(() => {
    const byYear = new Map();
    attendance.forEach(rec => {
      const offering = offeringById[rec.classOfferingId];
      const syId  = offering?.schoolYearId  ?? "unknown";
      const semId = offering?.semesterId    ?? "unknown";
      const ofId  = rec.classOfferingId;
      if (!byYear.has(syId)) byYear.set(syId, new Map());
      const bySem = byYear.get(syId);
      if (!bySem.has(semId)) bySem.set(semId, new Map());
      const byOf = bySem.get(semId);
      if (!byOf.has(ofId)) byOf.set(ofId, []);
      byOf.get(ofId).push(rec);
    });
    return [...byYear.entries()].sort(([a], [b]) => {
      return (syById[b]?.label || "").localeCompare(syById[a]?.label || "");
    });
  }, [attendance, offeringById, syById]);

  /* Overall counts */
  const counts = useMemo(() => {
    const c = { PRESENT: 0, LATE: 0, ABSENT: 0, EXCUSED: 0 };
    attendance.forEach(r => { if (c[r.status] != null) c[r.status]++; });
    return c;
  }, [attendance]);

  function toggleExpand(key) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  if (loading) return <TabSpinner label="Loading attendance records…" />;
  if (error)   return <TabError message={error} />;
  if (attendance.length === 0) return (
    <TabEmpty
      icon="📋"
      title="No attendance records"
      sub="Attendance entries made by teachers will appear here once recorded."
    />
  );

  return (
    <div className={s.root}>
      {/* Overall summary */}
      <div className={s.summaryBar}>
        {Object.entries(counts).map(([status, count]) => {
          const style = STATUS_COLOR[status] || {};
          return (
            <div
              key={status}
              className={s.summaryItem}
              style={{ background: style.bg, color: style.color }}>
              <span className={s.summaryCount}>{count}</span>
              <span className={s.summaryLabel}>{status}</span>
            </div>
          );
        })}
        <div className={s.summaryItem} style={{ background: "#f5f0e8", color: "#6b3a30" }}>
          <span className={s.summaryCount}>{attendance.length}</span>
          <span className={s.summaryLabel}>TOTAL</span>
        </div>
      </div>

      {/* Timeline */}
      {grouped.map(([syId, bySem], gIdx) => {
        const sy = syById[syId];
        return (
          <div key={syId} className={s.yearBlock} style={{ animationDelay: `${gIdx * 60}ms` }}>
            <div className={s.yearHead}>
              <span className={s.yearDot} aria-hidden="true" />
              <span className={s.yearLabel}>{sy ? syLabel(sy.label) : "Unknown Year"}</span>
            </div>

            {[...bySem.entries()].map(([semId, byOff]) => {
              const sem = semById[semId];
              return (
                <div key={semId} className={s.semBlock}>
                  {sem && <div className={s.semLabel}>{sem.label}</div>}
                  {[...byOff.entries()].map(([ofId, recs]) => {
                    const off     = offeringById[ofId];
                    const subj    = off?.subject?.title || off?.subjectTitle || `Class #${ofId}`;
                    const ofCounts = { PRESENT: 0, LATE: 0, ABSENT: 0, EXCUSED: 0 };
                    recs.forEach(r => { if (ofCounts[r.status] != null) ofCounts[r.status]++; });
                    const key     = `${syId}-${semId}-${ofId}`;
                    const open    = expanded.has(key);
                    const sorted  = [...recs].sort(
                      (a, b) => new Date(b.sessionDate) - new Date(a.sessionDate)
                    );
                    return (
                      <div key={ofId} className={s.offeringBlock}>
                        <button
                          className={`${s.offeringHead} ${open ? s.offeringHeadOpen : ""}`}
                          onClick={() => toggleExpand(key)}
                          aria-expanded={open}>
                          <div className={s.offeringLeft}>
                            <span className={s.subjectName}>{subj}</span>
                            <div className={s.ofCounts}>
                              {Object.entries(ofCounts).map(([st, ct]) => ct > 0 && (
                                <span
                                  key={st}
                                  className={s.ofCountChip}
                                  style={{
                                    background: STATUS_COLOR[st]?.bg,
                                    color: STATUS_COLOR[st]?.color,
                                  }}>
                                  {ct} {st}
                                </span>
                              ))}
                            </div>
                          </div>
                          <span className={s.chevron} aria-hidden="true">
                            {open ? "▲" : "▼"}
                          </span>
                        </button>
                        {open && (
                          <div className={s.recordList}>
                            {sorted.map(rec => {
                              const style = STATUS_COLOR[rec.status] || {};
                              return (
                                <div key={rec.id} className={s.record}>
                                  <span className={s.recDate}>
                                    {fmtDateShort(rec.sessionDate)}
                                  </span>
                                  <span
                                    className={s.recStatus}
                                    style={{ background: style.bg, color: style.color }}>
                                    {rec.status}
                                  </span>
                                  {rec.remarks && (
                                    <span className={s.recRemarks}>{rec.remarks}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
